import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useDataProvider, useNotify, useTranslate } from "ra-core";
import { useState, type FormEvent } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";

import type { CrmDataProvider } from "../providers/types";
import type { CalendarInfo, MailAccount } from "./mailTypes";
import { MailStatus } from "./MailStatus";
import {
  MAIL_ACCOUNT_QUERY_KEY,
  mailErrorKey,
  useMailAccountMutation,
} from "./useMailAccount";

/**
 * « Agenda » card: logs in to the Infomaniak calendar server (CalDAV), then
 * lets the user pick the calendar OPTICERCLE writes events to.
 */
export const CalendarSection = ({
  account,
}: {
  account: MailAccount | null;
}) => {
  const translate = useTranslate();
  const notify = useNotify();
  const queryClient = useQueryClient();
  const dataProvider = useDataProvider<CrmDataProvider>();
  const status = account?.calendar_status ?? "disconnected";
  const [username, setUsername] = useState(account?.caldav_username ?? "");
  const [password, setPassword] = useState("");
  const [isConnecting, setConnecting] = useState(false);
  const [isChoosing, setChoosing] = useState(status === "pending");
  const [freshCalendars, setFreshCalendars] = useState<CalendarInfo[] | null>(
    null,
  );
  const [selectedUrl, setSelectedUrl] = useState(
    account?.caldav_calendar_url ?? "",
  );

  // The list is fetched again when the user comes back to choose a calendar.
  const { data: storedCalendars, isFetching } = useQuery({
    queryKey: ["mailCalendars"],
    queryFn: () => dataProvider.listCalendars(),
    enabled: isChoosing && !freshCalendars && status !== "disconnected",
    meta: { persist: false },
  });
  const calendars = freshCalendars ?? storedCalendars ?? [];

  const select = useMailAccountMutation(
    (provider, url: string) => provider.selectCalendar(url),
    "crm.mail.calendar.saved",
  );
  const disconnect = useMailAccountMutation(
    (provider) => provider.disconnectCalendar(),
    "crm.mail.calendar.disconnected",
  );

  const handleConnect = async (event: FormEvent) => {
    event.preventDefault();
    setConnecting(true);
    try {
      const result = await dataProvider.connectCalendar({
        username: username.trim(),
        password,
      });
      queryClient.setQueryData(MAIL_ACCOUNT_QUERY_KEY, result.account);
      setFreshCalendars(result.calendars);
      setSelectedUrl(result.calendars[0]?.url ?? "");
      setPassword("");
      setChoosing(true);
    } catch (error) {
      notify(mailErrorKey(error), { type: "error" });
    } finally {
      setConnecting(false);
    }
  };

  const handleSelect = () =>
    select.mutate(selectedUrl, { onSuccess: () => setChoosing(false) });

  return (
    <Card>
      <CardContent className="flex flex-col gap-4">
        <div className="flex items-start justify-between gap-4">
          <div>
            <h2 className="text-lg font-semibold">
              {translate("crm.mail.calendar.title")}
            </h2>
            <p className="text-sm text-muted-foreground">
              {status === "connected" && account?.caldav_calendar_name
                ? translate("crm.mail.calendar.connected_to", {
                    name: account.caldav_calendar_name,
                  })
                : translate("crm.mail.calendar.help")}
            </p>
          </div>
          <MailStatus
            kind="calendar"
            status={status}
            error={account?.calendar_error}
          />
        </div>

        {isChoosing && status !== "disconnected" ? (
          <div className="flex flex-col gap-3">
            <p className="text-sm font-medium">
              {translate("crm.mail.calendar.choose")}
            </p>
            {isFetching ? null : calendars.length === 0 ? (
              <p className="text-sm text-muted-foreground">
                {translate("crm.mail.calendar.none")}
              </p>
            ) : (
              <RadioGroup value={selectedUrl} onValueChange={setSelectedUrl}>
                {calendars.map((calendar) => (
                  <div key={calendar.url} className="flex items-center gap-2">
                    <RadioGroupItem value={calendar.url} id={calendar.url} />
                    <Label htmlFor={calendar.url} className="font-normal">
                      {calendar.name}
                    </Label>
                  </div>
                ))}
              </RadioGroup>
            )}
            <div className="flex justify-end">
              <Button
                type="button"
                disabled={!selectedUrl || select.isPending}
                onClick={handleSelect}
              >
                {translate("crm.mail.calendar.use")}
              </Button>
            </div>
          </div>
        ) : status === "connected" || status === "error" ? (
          <div className="flex flex-wrap justify-end gap-2">
            <Button
              type="button"
              variant="outline"
              disabled={disconnect.isPending}
              onClick={() => disconnect.mutate(undefined)}
            >
              {translate("crm.mail.disconnect")}
            </Button>
            <Button
              type="button"
              variant="outline"
              onClick={() => setChoosing(true)}
            >
              {translate("crm.mail.calendar.change")}
            </Button>
          </div>
        ) : (
          <form className="flex flex-col gap-4" onSubmit={handleConnect}>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="flex flex-col gap-1.5">
                <Label htmlFor="calendar-username">
                  {translate("crm.mail.calendar.username")}
                </Label>
                <Input
                  id="calendar-username"
                  autoComplete="username"
                  placeholder="AB12345"
                  value={username}
                  onChange={(event) => setUsername(event.target.value)}
                  required
                />
              </div>
              <div className="flex flex-col gap-1.5">
                <Label htmlFor="calendar-password">
                  {translate("crm.mail.calendar.password")}
                </Label>
                <Input
                  id="calendar-password"
                  type="password"
                  autoComplete="new-password"
                  value={password}
                  onChange={(event) => setPassword(event.target.value)}
                  required
                />
              </div>
            </div>
            <div className="flex justify-end">
              <Button type="submit" disabled={isConnecting}>
                {translate(
                  isConnecting
                    ? "crm.mail.checking"
                    : "crm.mail.calendar.connect",
                )}
              </Button>
            </div>
          </form>
        )}
      </CardContent>
    </Card>
  );
};
